import { z } from 'zod';

const emptyStringToNull = z.preprocess((val) => {
  if (val === '' || val === null || val === undefined) {
    return null;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed === '') {
      return null;
    }
    const num = Number(trimmed);
    return isNaN(num) ? val : num;
  }
  return val;
}, z.number().nullable());

export const planTemplateFormSchema = z
  .object({
    name: z.string().min(1, 'Plan name is required'),
    description: z.string().optional(),
    incident_type: z.string().min(1, 'Incident type is required'),
    reunification_mode: z.string().min(1, 'Reunification mode is required'),
    scale: z.string().min(1, 'Plan scale is required'),
    default_leo_interview: z.boolean().optional(),
    default_counseling: z.boolean().optional(),
    special_procedures: z.string().optional(),
    estimated_duration_hours: emptyStringToNull
      .pipe(z.number().min(0, 'Duration must be positive').nullable())
      .optional(),
    is_published: z.boolean().optional(),
    guardian_verification_required: z.boolean().optional(),
    require_id_verification: z.boolean().optional(),
    require_photo_capture: z.boolean().optional(),
    require_signature: z.boolean().optional(),
    notify_on_activation: z.boolean().optional(),
    notification_channels: z.array(z.string()).optional(),
  })
  .refine(
    (data) =>
      !data.notify_on_activation ||
      (data.notification_channels && data.notification_channels.length >= 1),
    {
      message: 'At least one notification channel is required',
      path: ['notification_channels'],
    },
  );

export const siteFormSchema = z.object({
  name: z.string().min(1, 'Site name is required'),
  site_type: z.string().min(1, 'Site type is required'),
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip_code: z.string().optional(),
  latitude: emptyStringToNull
    .pipe(z.number().min(-90, 'Invalid latitude').max(90, 'Invalid latitude').nullable())
    .optional(),
  longitude: emptyStringToNull
    .pipe(
      z.number().min(-180, 'Invalid longitude').max(180, 'Invalid longitude').nullable(),
    )
    .optional(),
  total_capacity: emptyStringToNull
    .pipe(z.number().min(0, 'Capacity must be positive').nullable())
    .optional(),
  indoor_capacity: emptyStringToNull
    .pipe(z.number().min(0, 'Indoor capacity must be positive').nullable())
    .optional(),
  outdoor_capacity: emptyStringToNull
    .pipe(z.number().min(0, 'Outdoor capacity must be positive').nullable())
    .optional(),
  parking_capacity: emptyStringToNull
    .pipe(z.number().min(0, 'Parking capacity must be positive').nullable())
    .optional(),
  access_instructions: z.string().optional(),
  accessibility_notes: z.string().optional(),
  contact_name: z.string().optional(),
  contact_phone: z.string().optional(),
  contact_email: z.string().email('Invalid email address').optional().or(z.literal('')),
  is_active: z.boolean().optional(),
  is_verified: z.boolean().optional(),
  school: z.number().nullable().optional(),
});

export const configurableTypeFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  code: z.string().trim().min(1, 'Code is required'),
  description: z.string().default(''),
  icon: z.string().default(''),
  color: z.string().default(''),
  sort_order: z
    .preprocess(
      (val) => (val === '' || val === null || val === undefined ? '' : Number(val)),
      z.union([z.number().min(0, 'Sort order must be positive'), z.literal('')]),
    ),
  is_active: z.boolean().default(true),
});

